// frontend/src/components/Layout.js - UPDATED WITH FIXED TECHNICIAN PARTS MENU
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  IconButton, Tooltip, FormControl, Select, MenuItem, InputLabel, Chip,
  useMediaQuery, Menu, ListItemIcon, ListItemText, Divider
} from '@mui/material';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import CheckIcon from '@mui/icons-material/Check';
import MenuIcon from '@mui/icons-material/Menu';
import { useThemeMode } from '../context/ThemeContext';
import { useBranchFilter } from '../context/BranchContext';
import { getCurrentBranchLabel, isFullAdmin, canAccessAllBranches, getCurrentUserName } from '../utils/auth';
import { trackLoginActivity } from '../services/api';
import './Layout.css';

const getUserFromStorage = () => {
  return {
    name: getCurrentUserName(),
    role: localStorage.getItem('erp_user_role') || 'staff',
    branchId: localStorage.getItem('erp_branch_id'),
    branchLabel: getCurrentBranchLabel()
  };
};

const normalizeMenuRole = (role) => {
  const r = String(role || 'staff').toLowerCase().trim().replace(/[\s-]+/g, '_');
  if (r === 'technical_cordinator' || r === 'tech_coordinator') return 'technical_coordinator';
  return r;
};

/** Super admin always sees every menu item */
const hasMenuAccess = (roles, userRole) => {
  const r = normalizeMenuRole(userRole);
  if (r === 'super_admin') return true;
  if (r === 'admin') {
    // Admin sees all except items marked superAdminOnly
    if (Array.isArray(roles) && roles.includes('__super_only__')) return false;
    return true;
  }
  return Array.isArray(roles) && roles.map(normalizeMenuRole).includes(r);
};

function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { mode, setMode, isDark } = useThemeMode();
  const { branches, selectedBranchId, setSelectedBranchId, selectedBranch } = useBranchFilter();
  const isMobile = useMediaQuery('(max-width: 900px)');
  const [sidebarOpen, setSidebarOpen] = useState(!window.matchMedia('(max-width: 900px)').matches);
  const [user, setUser] = useState(getUserFromStorage());
  const [expandedMenus, setExpandedMenus] = useState([]);
  const [themeMenuAnchor, setThemeMenuAnchor] = useState(null);

  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
    else setSidebarOpen(true);
  }, [isMobile]);

  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [location.pathname, isMobile]);

  // Track post-login navigation for Login History (ignore errors / 502)
  useEffect(() => {
    const token = localStorage.getItem('erp_auth_token');
    if (!token || location.pathname === '/login') return;
    const sessionId = localStorage.getItem('erp_login_session_id');
    trackLoginActivity({
      action: location.pathname + (location.search || ''),
      method: 'NAV',
      session_id: sessionId
    }).catch(() => { /* backend down or no history module — ignore */ });
  }, [location.pathname, location.search]);
  
  // Check if user is authenticated on component mount
  useEffect(() => {
    const token = localStorage.getItem('erp_auth_token');
    if (!token) {
      navigate('/login');
    }
    
    // Update user state from storage
    setUser(getUserFromStorage());
    
    // Automatically expand menu based on current path
    const path = location.pathname;
    let menusToExpand = [];
    
    if (path.startsWith('/accounting')) menusToExpand.push('accounting');
    if (path.startsWith('/repairs') || path.startsWith('/ssi-technical')) menusToExpand.push('repairs');
    if (path.startsWith('/rentals')) menusToExpand.push('rentals');
    if (
      path.startsWith('/pos') ||
      path.startsWith('/grn') ||
      path.startsWith('/products') ||
      path.startsWith('/customers') ||
      path.startsWith('/reports') ||
      path.startsWith('/office-issue')
    ) {
      menusToExpand.push('pos');
    }
    if (
      path.startsWith('/branches') ||
      path.startsWith('/branch/') ||
      path.startsWith('/users') ||
      path.startsWith('/login-history') ||
      path.startsWith('/settings')
    ) {
      menusToExpand.push('branch');
    }
    
    setExpandedMenus(menusToExpand);
  }, [navigate, location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem('erp_auth_token');
    localStorage.removeItem('erp_user_role');
    localStorage.removeItem('erp_username');
    localStorage.removeItem('erp_branch_id');
    localStorage.removeItem('erp_branch_name');
    localStorage.removeItem('erp_branch_code');
    localStorage.removeItem('erp_user_id');
    localStorage.removeItem('erp_user_email');
    localStorage.removeItem('erp_user');
    localStorage.removeItem('erp_login_session_id');
    navigate('/login');
  };

  const toggleMenu = (menu) => {
    setExpandedMenus(prev => 
      prev.includes(menu) 
        ? prev.filter(m => m !== menu)
        : [...prev, menu]
    );
  };

  // Navigation groups (super_admin sees everything via hasMenuAccess)
  const ALL = ['admin', 'super_admin', 'staff', 'branch_manager', 'branch_staff', 'manager', 'technician', 'technical_coordinator', 'admin_officer', 'cashier', 'accountant', 'super_accountant'];
  const ADMINS = ['admin', 'super_admin'];
  const BRANCH_SHIFTERS = ['admin', 'super_admin', 'super_accountant'];
  const POS_CORE = ['admin', 'super_admin', 'staff', 'branch_manager', 'branch_staff', 'manager', 'admin_officer'];
  const REPAIR_FULL = ['admin', 'super_admin', 'staff', 'branch_manager', 'branch_staff', 'manager', 'technical_coordinator', 'admin_officer'];
  const REPAIR_WORKSHOP = [...REPAIR_FULL, 'technician'];
  const REPAIR_NO_REPORT = REPAIR_FULL; // report/outsource-tech master excluded below

  const menuItems = [
    {
      path: '/dashboard',
      icon: '📊',
      label: 'Dashboard',
      roles: ALL,
      isMainMenu: true
    },

    // POS group
    {
      type: 'group',
      key: 'pos',
      icon: '🛒',
      label: 'POS',
      roles: [...POS_CORE, 'cashier', 'accountant', 'super_accountant'],
      children: [
        { path: '/pos', icon: '🛒', label: 'POS', roles: [...POS_CORE, 'cashier'] },
        { path: '/pos/sales', icon: '📋', label: 'POS Sales', roles: [...POS_CORE, 'cashier'] },
        { path: '/grn', icon: '📥', label: 'GRN', roles: POS_CORE },
        { path: '/products', icon: '📦', label: 'Products', roles: POS_CORE },
        { path: '/customers', icon: '👥', label: 'Customers', roles: POS_CORE },
        { path: '/reports', icon: '📈', label: 'Reports', roles: [...ADMINS, 'super_accountant'] },
        { path: '/office-issue', icon: '🏢', label: 'Office Issue', roles: [...POS_CORE, 'accountant', 'super_accountant'] }
      ]
    },

    // Branch group
    {
      type: 'group',
      key: 'branch',
      icon: '🏢',
      label: 'Branch',
      roles: [...ADMINS, 'branch_manager', 'super_accountant'],
      children: [
        { path: '/branches', icon: '🏢', label: 'Branches', roles: ADMINS },
        { path: '/branches/users', icon: '👤', label: 'Branch Users', roles: BRANCH_SHIFTERS },
        { path: '/branch/staff', icon: '👥', label: 'My Branch Staff', roles: ['branch_manager', 'super_admin', 'admin'] },
        { path: '/users', icon: '👥', label: 'User Management', roles: ADMINS },
        { path: '/login-history', icon: '🛡️', label: 'Login History', roles: ['__super_only__'] },
        { path: '/settings', icon: '⚙️', label: 'Settings', roles: ADMINS }
      ]
    },

    // Repair Management
    {
      type: 'group',
      key: 'repairs',
      icon: '🔧',
      label: 'Repair Management',
      roles: [...REPAIR_WORKSHOP, 'branch_staff'],
      children: [
        { path: '/repairs', icon: '📋', label: 'Service Requests / JOB', roles: REPAIR_NO_REPORT },
        { path: '/repairs/new', icon: '➕', label: 'New Request', roles: REPAIR_NO_REPORT },
        { path: '/repairs/workshop', icon: '🏭', label: 'Workshop', roles: REPAIR_WORKSHOP },
        { path: '/repairs/ready-to-collect', icon: '📦', label: 'Ready to Collect', roles: REPAIR_WORKSHOP },
        { path: '/repairs/repair-done', icon: '✅', label: 'Closed Job', roles: REPAIR_NO_REPORT },
        { path: '/ssi-technical', icon: '🧪', label: 'SSI Technical', roles: REPAIR_WORKSHOP },
        { path: '/repairs/outsource', icon: '🚚', label: 'Outsource Repairs', roles: REPAIR_NO_REPORT },
        { path: '/repairs/qa', icon: '✅', label: 'QA', roles: REPAIR_NO_REPORT },
        { path: '/repairs/part-requests', icon: '🔩', label: 'Part Requests', roles: [...REPAIR_NO_REPORT, 'technician'] },
        { path: '/repairs/invoices', icon: '🧾', label: 'Repair Invoices', roles: REPAIR_NO_REPORT },
        { path: '/repairs/customers', icon: '👤', label: 'Customer Profile', roles: REPAIR_NO_REPORT },
        { path: '/repairs/inhouse-technicians', icon: '🧑‍🔧', label: 'In-House Technicians', roles: [...ADMINS, 'technical_coordinator', 'admin_officer'] },
        { path: '/repairs/outsource-technicians', icon: '👷', label: 'Outsource Technicians', roles: ADMINS },
        { path: '/repairs/reports', icon: '📈', label: 'Repair Report', roles: ADMINS }
      ]
    },

    {
      type: 'group',
      key: 'rentals',
      icon: '📅',
      label: 'Rental',
      roles: [...POS_CORE, 'cashier', 'accountant', 'super_accountant'],
      children: [
        { path: '/rentals/dashboard', icon: '📊', label: 'Dashboard', roles: [...POS_CORE, 'cashier', 'accountant', 'super_accountant'] },
        { path: '/rentals', icon: '📋', label: 'Rentals', roles: [...POS_CORE, 'cashier', 'accountant', 'super_accountant'] },
        { path: '/rentals/new', icon: '➕', label: 'Create Rental', roles: [...POS_CORE, 'cashier', 'accountant', 'super_accountant'] },
        { path: '/rentals/invoices', icon: '🧾', label: 'Invoices', roles: [...POS_CORE, 'cashier', 'accountant', 'super_accountant'] },
        { path: '/rentals/outside', icon: '🚛', label: 'Outside Rent', roles: [...POS_CORE, 'cashier', 'accountant', 'super_accountant'] },
        { path: '/rentals/report', icon: '📈', label: 'Reports', roles: [...POS_CORE, 'cashier', 'accountant', 'super_accountant'] },
        { path: '/rentals/agreement', icon: '📝', label: 'Rental Agreement', roles: [...POS_CORE, 'cashier', 'accountant', 'super_accountant'] },
        { path: '/rentals/track', icon: '🔍', label: 'Track Item', roles: [...POS_CORE, 'cashier', 'accountant', 'super_accountant'] },
        { path: '/rentals/customers', icon: '👤', label: 'Customer Profiles', roles: [...POS_CORE, 'cashier', 'accountant', 'super_accountant'] },
        { path: '/rentals/pending', icon: '💳', label: 'Pending Payments', roles: [...POS_CORE, 'cashier', 'accountant', 'super_accountant'] }
      ]
    },

    // Accounting
    {
      type: 'group',
      key: 'accounting',
      icon: '💼',
      label: 'Accounting',
      roles: [...ADMINS, 'accountant', 'super_accountant', 'manager'],
      children: [
        { path: '/accounting', icon: '📊', label: 'Dashboard', roles: ADMINS },
        { path: '/accounting/user-access', icon: '👤', label: 'User & Access', roles: ADMINS },
        { path: '/accounting/general-ledger', icon: '📒', label: 'General Ledger', roles: ADMINS },
        { path: '/accounting/accounts-receivable', icon: '💰', label: 'Accounts Receivable', roles: ADMINS },
        { path: '/accounting/accounts-payable', icon: '💵', label: 'Accounts Payable', roles: ADMINS },
        { path: '/accounting/bank-cash', icon: '🏦', label: 'Bank & Cash', roles: ADMINS },
        { path: '/accounting/inventory', icon: '📦', label: 'Inventory', roles: ADMINS },
        { path: '/accounting/fixed-assets', icon: '🏢', label: 'Fixed Assets', roles: ADMINS },
        { path: '/accounting/tax', icon: '🧾', label: 'Tax Management', roles: ADMINS },
        { path: '/accounting/reporting', icon: '📈', label: 'Reporting', roles: ADMINS },
        { path: '/accounting/budgeting', icon: '💼', label: 'Budgeting', roles: ADMINS },
        { path: '/accounting/integrations', icon: '🔗', label: 'Integrations', roles: ADMINS },
        { path: '/accounting/system-admin', icon: '⚙️', label: 'System Admin', roles: ADMINS },
        { path: '/accounting/security', icon: '🔐', label: 'Security', roles: ADMINS },
        { path: '/accounting/advanced', icon: '🚀', label: 'Advanced', roles: ADMINS }
      ]
    }
  ];

  // Individual menu items (for non-group items)
  const individualMenuItems = menuItems.filter(item => 
    !item.type && item.isMainMenu && hasMenuAccess(item.roles, user.role)
  );

  // Group menu items
  const groupMenuItems = menuItems.filter(item => 
    item.type === 'group' && hasMenuAccess(item.roles, user.role)
  );

  const getActiveMenuLabel = () => {
    // Check individual menu items first
    const current = individualMenuItems.find(item => item.path === location.pathname);
    if (current) return current.label;
    
    // Check group menu items children
    for (const group of groupMenuItems) {
      const child = group.children.find(item => item.path === location.pathname);
      if (child) return child.label;
    }
    
    // Handle dynamic routes
    if (location.pathname.startsWith('/invoices/')) {
      if (location.pathname.includes('/new')) return 'Create Invoice';
      if (location.pathname.includes('/edit/')) return 'Edit Invoice';
      return 'Invoice Details';
    }
    
    if (location.pathname.startsWith('/sales/')) {
      if (location.pathname.includes('/new')) return 'Create Sale';
      if (location.pathname.includes('/edit/')) return 'Edit Sale';
      return 'Sale Details';
    }
    
    if (location.pathname.startsWith('/quotations/')) {
      if (location.pathname.includes('/new')) return 'Create Quotation';
      if (location.pathname.includes('/edit/')) return 'Edit Quotation';
      return 'Quotation Details';
    }
    
    if (location.pathname.startsWith('/pos/sales')) {
      return 'POS Sales History';
    }
    
    if (location.pathname === '/pos') {
      return 'Point of Sale';
    }
    
    // Handle credit management routes
    if (location.pathname.startsWith('/credit/')) {
      if (location.pathname === '/credit/dashboard') return 'Credit Dashboard';
      if (location.pathname === '/credit/payments') return 'Payment Collection';
      if (location.pathname === '/credit/customers') return 'Credit Customers';
      if (location.pathname === '/credit/overdue') return 'Overdue Accounts';
      return 'Credit Management';
    }
    
    // Handle repair routes
    if (location.pathname.startsWith('/repairs')) {
      if (location.pathname === '/repairs/new') return 'Create Service Request';
      if (location.pathname === '/repairs/workshop') return 'Repair Workshop';
      if (location.pathname === '/repairs/ready-to-collect') return 'Ready to Collect';
      if (location.pathname === '/repairs/qa') return 'QA Check';
      if (location.pathname === '/repairs/repair-done') return 'Closed Job';
      if (location.pathname === '/repairs/inhouse-technicians') return 'In-House Technicians';
      if (location.pathname.startsWith('/ssi-technical')) return 'SSI Technical';
      if (location.pathname === '/repairs/part-requests') return 'Part Requests';
      if (location.pathname === '/repairs/invoices') return 'Repair Invoices';
      if (location.pathname === '/repairs/reports') return 'Repair Management Report';
      if (location.pathname.startsWith('/repairs/outsource/technician/')) return 'Outsource Technician';
      if (location.pathname === '/repairs/outsource') return 'Outsource Repairs';
      if (location.pathname === '/repairs/outsource-technicians') return 'Outsource Technicians';
      if (location.pathname.startsWith('/repairs/customers/')) return 'Customer Profile';
      if (location.pathname === '/repairs/customers') return 'Customer Profile';
      if (location.pathname.startsWith('/repairs/edit/')) return 'Edit Repair';
      if (location.pathname.startsWith('/repairs/')) return 'Service Request Details';
      return 'Repair Management';
    }

    if (location.pathname.startsWith('/rentals')) {
      if (location.pathname === '/rentals/dashboard') return 'Rental Dashboard';
      if (location.pathname === '/rentals/new') return 'Create Rental';
      if (location.pathname === '/rentals/track') return 'Track Rental Item';
      if (location.pathname === '/rentals/pending') return 'Pending Rental Payments';
      if (location.pathname === '/rentals/invoices') return 'Rental Invoices';
      if (location.pathname.startsWith('/rentals/invoices/')) return 'Rental Invoice';
      if (location.pathname === '/rentals/agreement') return 'Rental Agreement';
      if (location.pathname === '/rentals/report') return 'Rental Report';
      if (location.pathname.startsWith('/rentals/outside/')) return 'Outside Rent Profile';
      if (location.pathname === '/rentals/outside') return 'Outside Rent';
      if (location.pathname.startsWith('/rentals/customers/')) return 'Rental Customer Profile';
      if (location.pathname === '/rentals/customers') return 'Rental Customer Profiles';
      if (location.pathname.startsWith('/rentals/') && location.pathname !== '/rentals') return 'Rental Details';
      return 'Rentals';
    }

    if (location.pathname === '/branches') return 'Branch Management';
    if (location.pathname === '/branches/users') return 'Branch User Management';
    if (location.pathname === '/branch/staff') return 'Branch Staff';

    // Handle accounting routes
    if (location.pathname.startsWith('/accounting')) {
      if (location.pathname === '/accounting' || location.pathname === '/accounting/dashboard') return 'Accounting Dashboard';
      if (location.pathname.includes('/user-access')) return 'User & Access Management';
      if (location.pathname.includes('/general-ledger')) return 'General Ledger';
      if (location.pathname.includes('/accounts-receivable')) return 'Accounts Receivable';
      if (location.pathname.includes('/accounts-payable')) return 'Accounts Payable';
      if (location.pathname.includes('/bank-cash')) return 'Bank & Cash Management';
      if (location.pathname.includes('/fixed-assets')) return 'Fixed Assets';
      if (location.pathname.includes('/tax')) return 'Tax Management';
      if (location.pathname.includes('/reporting')) return 'Financial Reporting';
      if (location.pathname.includes('/budgeting')) return 'Budgeting & Forecasting';
      if (location.pathname.includes('/integrations')) return 'Integrations';
      if (location.pathname.includes('/system-admin')) return 'System Administration';
      if (location.pathname.includes('/security')) return 'Security & Compliance';
      if (location.pathname.includes('/advanced')) return 'Advanced Features';
      return 'Accounting Management';
    }
    
    return 'ERP System';
  };

  // Role badge colors
  const getRoleColor = (role) => {
    switch (normalizeMenuRole(role)) {
      case 'super_admin': return '#8e44ad';
      case 'admin': return '#ff6b6b';
      case 'branch_manager':
      case 'manager': return '#9b59b6';
      case 'technician': return '#e67e22';
      case 'branch_staff': return '#3498db';
      case 'staff': return '#4ecdc4';
      case 'cashier': return '#1abc9c';
      case 'customer': return '#45b7d1';
      default: return '#95a5a6';
    }
  };

  // Check if a menu item is active
  const isMenuItemActive = (path) => {
    return location.pathname === path || 
           (path !== '/dashboard' && location.pathname.startsWith(path));
  };

  // Check if a group menu is expanded
  const isMenuExpanded = (menuKey) => {
    return expandedMenus.includes(menuKey);
  };

  return (
    <div className={`layout-container theme-${mode} ${isDark ? 'dark-theme' : 'light-theme'}`}>
      {/* Sidebar */}
      {isMobile && sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'} ${isMobile ? 'mobile' : ''}`}>
        <div className="sidebar-header">
          <h2 className="sidebar-title">ERP System</h2>
          <button 
            className="sidebar-toggle" 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {sidebarOpen ? '◀' : '▶'}
          </button>
        </div>

        <div className="user-info">
          <div className="user-avatar" style={{ backgroundColor: getRoleColor(user.role) }}>
            {user.name.charAt(0).toUpperCase()}
          </div>
          {sidebarOpen && (
            <div className="user-details">
              <h3 className="user-name">{user.name}</h3>
              <p 
                className="user-role" 
                style={{ 
                  backgroundColor: getRoleColor(user.role),
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  display: 'inline-block'
                }}
              >
                {user.role.toUpperCase()}
              </p>
            </div>
          )}
        </div>

        <nav className="sidebar-nav">
          <ul>
            {/* Individual Menu Items */}
            {individualMenuItems.map((item) => (
              <li 
                key={item.path} 
                className={`nav-item ${isMenuItemActive(item.path) ? 'active' : ''}`}
              >
                <Link to={item.path} className="nav-link">
                  <span className="nav-icon">{item.icon}</span>
                  {sidebarOpen && <span className="nav-label">{item.label}</span>}
                </Link>
              </li>
            ))}
            
            {/* Group Menu Items */}
            {groupMenuItems.map((group) => (
              <li 
                key={group.key}
                className={`nav-group ${isMenuExpanded(group.key) ? 'expanded' : ''}`}
              >
                <div 
                  className="nav-group-header"
                  onClick={() => toggleMenu(group.key)}
                >
                  <span className="nav-icon">{group.icon}</span>
                  {sidebarOpen && (
                    <>
                      <span className="nav-label">{group.label}</span>
                      <span className="nav-arrow">
                        {isMenuExpanded(group.key) ? '▼' : '▶'}
                      </span>
                    </>
                  )}
                </div>
                
                {sidebarOpen && isMenuExpanded(group.key) && (
                  <ul className="nav-group-items">
                    {group.children
                      .filter(child => hasMenuAccess(child.roles, user.role))
                      .map((child) => (
                        <li 
                          key={child.path}
                          className={`nav-subitem ${isMenuItemActive(child.path) ? 'active' : ''}`}
                        >
                          <Link to={child.path} className="nav-link">
                            <span className="nav-icon">{child.icon}</span>
                            <span className="nav-label">{child.label}</span>
                          </Link>
                        </li>
                      ))
                    }
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </nav>

        <div className="sidebar-footer">
          <button onClick={handleLogout} className="logout-button">
            <span className="logout-icon">🚪</span>
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        <header className="main-header">
          <div className="header-left">
            {isMobile && (
              <IconButton
                className="mobile-menu-btn"
                onClick={() => setSidebarOpen(true)}
                aria-label="Open menu"
                size="small"
                sx={{ mr: 1, color: isDark ? '#e0e0e0' : '#2c3e50' }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <h1 className="page-title">{getActiveMenuLabel()}</h1>
          </div>
          <div className="header-actions">
            {canAccessAllBranches() && branches.length > 0 && (
              <Tooltip title="View filter only — does not move or shift data between branches">
                <FormControl size="small" sx={{ minWidth: 180 }} className="branch-selector">
                  <InputLabel id="branch-filter-label">View Branch</InputLabel>
                  <Select
                    labelId="branch-filter-label"
                    label="View Branch"
                    value={selectedBranchId || ''}
                    onChange={(e) => setSelectedBranchId(e.target.value)}
                    className="branch-select"
                  >
                    <MenuItem value="">All Branches</MenuItem>
                    {branches.map((b) => (
                      <MenuItem key={b.id} value={String(b.id)}>
                        {b.name}{b.branch_code ? ` (${b.branch_code})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Tooltip>
            )}
            <Tooltip title="Theme: White / Light / Dark">
              <IconButton
                onClick={(e) => setThemeMenuAnchor(e.currentTarget)}
                className="theme-toggle-btn"
                size="small"
                sx={{ mr: 1, color: isDark ? '#e0e0e0' : '#1a1a1a' }}
              >
                {mode === 'dark' ? <DarkModeIcon /> : mode === 'white' ? <LightModeIcon /> : <WbSunnyOutlinedIcon />}
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={themeMenuAnchor}
              open={Boolean(themeMenuAnchor)}
              onClose={() => setThemeMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem disabled sx={{ opacity: 1, fontWeight: 700, fontSize: 12 }}>
                Choose theme
              </MenuItem>
              <Divider />
              {[
                { id: 'white', label: 'White', hint: 'Pure white background', icon: <LightModeIcon fontSize="small" /> },
                { id: 'light', label: 'Light', hint: 'Soft gray background', icon: <WbSunnyOutlinedIcon fontSize="small" /> },
                { id: 'dark', label: 'Dark', hint: 'Dark panels · light text', icon: <DarkModeIcon fontSize="small" /> }
              ].map((opt) => (
                <MenuItem
                  key={opt.id}
                  selected={mode === opt.id}
                  onClick={() => {
                    setMode(opt.id);
                    setThemeMenuAnchor(null);
                  }}
                >
                  <ListItemIcon>{opt.icon}</ListItemIcon>
                  <ListItemText
                    primary={opt.label}
                    secondary={opt.hint}
                    primaryTypographyProps={{ fontWeight: mode === opt.id ? 700 : 500 }}
                  />
                  {mode === opt.id ? <CheckIcon fontSize="small" color="primary" /> : null}
                </MenuItem>
              ))}
            </Menu>
            <span className="welcome-text">Welcome, <strong>{user.name}</strong>!</span>
            {selectedBranch && canAccessAllBranches() && (
              <Chip
                label={`${selectedBranch.name}${selectedBranch.branch_code ? ` (${selectedBranch.branch_code})` : ''}`}
                size="small"
                color="primary"
                variant="outlined"
                className="branch-badge"
              />
            )}
            {!canAccessAllBranches() && user.branchLabel && (
              <Chip
                label={user.branchLabel}
                size="small"
                color="primary"
                variant="outlined"
                className="branch-badge"
                sx={{ ml: 1 }}
              />
            )}
            <div className="user-role-badge" style={{ 
              backgroundColor: getRoleColor(user.role),
              color: 'white',
              padding: '4px 12px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: 'bold',
              marginLeft: '10px'
            }}>
              {user.role.replace(/_/g, ' ').toUpperCase()}
            </div>
            <div className="notifications-container">
              <button className="notification-btn">
                <span className="notification-icon">🔔</span>
                <span className="notification-badge">3</span>
              </button>
            </div>
          </div>
        </header>
        
        {/* Remount page when View Branch filter changes so lists reload */}
        <div className="content-area" key={`branch-view-${selectedBranchId || 'all'}`}>
          {children}
        </div>
      </main>
    </div>
  );
}

export default Layout;